import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { fetchUserContext, restoreUserSession } from "../utils/utils";

export const UserContext = createContext(null);

const ADDRESS_SKIPPED_STORAGE_PREFIX = "civix:addressSkipped:";

function addressSkippedStorageKey(uid) {
  return `${ADDRESS_SKIPPED_STORAGE_PREFIX}${uid}`;
}

function readAddressSkippedFromStorage(uid) {
  if (!uid) return false;
  try {
    return localStorage.getItem(addressSkippedStorageKey(uid)) === "true";
  } catch {
    return false;
  }
}

function writeAddressSkippedToStorage(uid, skipped) {
  if (!uid) return;
  try {
    if (skipped) {
      localStorage.setItem(addressSkippedStorageKey(uid), "true");
    } else {
      localStorage.removeItem(addressSkippedStorageKey(uid));
    }
  } catch {
    /* Safari private mode, quota exceeded, etc. */
  }
}

function clearAddressSkippedStorage(uid) {
  if (!uid) return;
  try {
    localStorage.removeItem(addressSkippedStorageKey(uid));
  } catch {
    /* Safari private mode, quota exceeded, etc. */
  }
}

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);
  const [loadingUser, setLoadingUser] = useState(true);
  const [userId, setUserId] = useState(null);
  const [dnaUserId, setDnaUserId] = useState(null);

  const [userRepsBioguideID, setUserRepsBioguideID] = useState(null);
  const [userRepsName, setUserRepsName] = useState(null);
  const [userSenOneBioguideID, setUserSenOneBioguideID] = useState(null);
  const [userSenTwoBioguideID, setUserSenTwoBioguideID] = useState(null);
  const [userSenOneName, setUserSenOneName] = useState(null);
  const [userSenTwoName, setUserSenTwoName] = useState(null);

  const [verifiedUser, setVerifiedUser] = useState(false);
  const [userDistrict, setUserDistrict] = useState(null);
  const [userState, setUserState] = useState(null);
  const [userZipcode, setUserZipcode] = useState(null);
  const [userFirstName, setUserFirstName] = useState(null);
  const [userLastName, setUserLastName] = useState(null);

  const [accountTypeName, setAccountTypeName] = useState(null);
  const [hasSelectedAccountType, setHasSelectedAccountType] = useState(false);
  const [userUsername, setUserUsername] = useState(null);
  const [isProfilePublic, setIsProfilePublic] = useState(false);
  const [requiresUsernameHint, setRequiresUsernameHint] = useState(null);
  const [canCreateEvent, setCanCreateEvent] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [entityName, setEntityName] = useState(null);
  const [municipalityName, setMunicipalityName] = useState(null);
  const [municipalityDepartment, setMunicipalityDepartment] = useState(null);
  const [isStripeVerified, setIsStripeVerified] = useState(false);
  const [politicianVerificationStatus, setPoliticianVerificationStatus] = useState(null);
  const [verificationRejectionReason, setVerificationRejectionReason] = useState(null);
  const [citizenManualVerificationStatus, setCitizenManualVerificationStatus] = useState(null);
  const [hasAcceptedCommunityGuidelines, setHasAcceptedCommunityGuidelines] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [publicOfficeStatus, setPublicOfficeStatus] = useState(null);
  const [publicOfficeLevel, setPublicOfficeLevel] = useState(null);
  const [publicOfficePosition, setPublicOfficePosition] = useState(null);
  const [addressSkipped, setAddressSkippedState] = useState(false);

  const firebaseUidRef = useRef(null);

  const setAddressSkipped = useCallback((skipped) => {
    setAddressSkippedState(skipped);
    writeAddressSkippedToStorage(firebaseUidRef.current, skipped);
  }, []);

  const resetUserState = useCallback(() => {
    clearAddressSkippedStorage(firebaseUidRef.current);
    firebaseUidRef.current = null;

    setUser(null);
    setUserId(null);
    setDnaUserId(null);

    setUserRepsBioguideID(null);
    setUserRepsName(null);
    setUserSenOneBioguideID(null);
    setUserSenTwoBioguideID(null);
    setUserSenOneName(null);
    setUserSenTwoName(null);

    setVerifiedUser(false);
    setUserDistrict(null);
    setUserState(null);
    setUserZipcode(null);
    setUserFirstName(null);
    setUserLastName(null);

    setAccountTypeName(null);
    setHasSelectedAccountType(false);
    setUserUsername(null);
    setIsProfilePublic(false);
    setRequiresUsernameHint(null);
    setCanCreateEvent(false);
    setProfileImage(null);
    setEntityName(null);
    setMunicipalityName(null);
    setMunicipalityDepartment(null);
    setIsStripeVerified(false);
    setPoliticianVerificationStatus(null);
    setVerificationRejectionReason(null);
    setCitizenManualVerificationStatus(null);
    setHasAcceptedCommunityGuidelines(false);
    setHasCompletedOnboarding(false);
    setPublicOfficeStatus(null);
    setPublicOfficeLevel(null);
    setPublicOfficePosition(null);
    setAddressSkippedState(false);
  }, []);

  const loadUserContext = useCallback(async () => {
    try {
      const ctx = await fetchUserContext();
      if (!ctx) {
        resetUserState();
        return false;
      }

      setDnaUserId(ctx.userDnaId);
      setUserRepsBioguideID(ctx.bioguideId);
      setUserRepsName(ctx.userRepsName);
      setUserSenOneName(ctx.userSenOneName);
      setUserSenTwoName(ctx.userSenTwoName);
      setUserSenOneBioguideID(ctx.userSenOneBioguideID);
      setUserSenTwoBioguideID(ctx.userSenTwoBioguideID);
      setVerifiedUser(ctx.verifiedUser);
      setUserDistrict(ctx.userDistrict);
      setUserState(ctx.userState);
      setUserZipcode(ctx.zipcode || null);
      setUserFirstName(ctx.userFirstName);
      setUserLastName(ctx.userLastName);

      setAccountTypeName(ctx.accountTypeName);
      setHasSelectedAccountType(Boolean(ctx.hasSelectedAccountType));
      setUserUsername(ctx.username);
      setIsProfilePublic(Boolean(ctx.isProfilePublic));
      setRequiresUsernameHint(
        typeof ctx.requiresUsername === "boolean" ? ctx.requiresUsername : null
      );
      setCanCreateEvent(Boolean(ctx.canCreateEvent));
      setProfileImage(ctx.profileImage);
      setEntityName(ctx.entityName);
      setMunicipalityName(ctx.municipalityName ?? null);
      setMunicipalityDepartment(ctx.municipalityDepartment ?? null);
      setIsStripeVerified(Boolean(ctx.isStripeVerified));
      setPoliticianVerificationStatus(ctx.politicianVerificationStatus ?? null);
      setVerificationRejectionReason(ctx.verificationRejectionReason ?? null);
      setCitizenManualVerificationStatus(ctx.citizenManualVerificationStatus ?? null);
      setHasAcceptedCommunityGuidelines(Boolean(ctx.hasAcceptedCommunityGuidelines));
      setPublicOfficeStatus(ctx.publicOfficeStatus ?? null);
      setPublicOfficeLevel(ctx.publicOfficeLevel ?? null);
      setPublicOfficePosition(ctx.publicOfficePosition ?? null);
      setHasCompletedOnboarding(
        Boolean(ctx.hasSelectedAccountType && ctx.username && ctx.bioguideId)
      );

      const appliedViewOnlySkip =
        !ctx.bioguideId &&
        Boolean(ctx.hasSelectedAccountType && ctx.username);

      if (ctx.bioguideId) {
        setAddressSkipped(false);
      } else if (appliedViewOnlySkip) {
        // Web equivalent of mobile view-only mode: completed onboarding, no rep on file
        setAddressSkipped(true);
      } else if (
        readAddressSkippedFromStorage(firebaseUidRef.current)
      ) {
        // Skip before username exists in API — view-only does not run yet
        setAddressSkipped(true);
      }

      return ctx;
    } catch (err) {
      console.error("Error loading user context:", err);
      resetUserState();
      return false;
    }
  }, [resetUserState, setAddressSkipped]);

  const refreshFirebaseUser = useCallback(async () => {
    const auth = getAuth();
    const current = auth.currentUser;
    if (!current) return null;

    await current.reload();
    await current.getIdToken(true);

    const refreshed = auth.currentUser;
    if (refreshed) {
      firebaseUidRef.current = refreshed.uid;
      setUser(refreshed);
    }
    return refreshed;
  }, []);

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      setLoadingUser(true);

      if (!fbUser) {
        resetUserState();
        setLoadingUser(false);
        return;
      }

      firebaseUidRef.current = fbUser.uid;
      setUser(fbUser);

      try {
        await restoreUserSession(setUserId, setUserRepsBioguideID);
        await loadUserContext();
      } catch (err) {
        console.error("Error restoring user session:", err);
      }

      setLoadingUser(false);
    });

    return () => unsubscribe();
  }, [loadUserContext, resetUserState]);

  const requiresUsername =
    typeof requiresUsernameHint === "boolean"
      ? requiresUsernameHint
      : Boolean(user?.emailVerified) &&
        Boolean(hasSelectedAccountType) &&
        !userUsername;

  const hasUsername = Boolean(userUsername);
  const canAccessDashboard =
    Boolean(hasSelectedAccountType) && hasUsername;

  return (
    <UserContext.Provider
      value={{
        user,
        loadingUser,
        userId,
        setUserId,
        dnaUserId,

        userRepsBioguideID,
        setUserRepsBioguideID,
        userRepsName,
        setUserRepsName,

        userSenOneName,
        setUserSenOneName,
        userSenTwoName,
        setUserSenTwoName,
        userSenOneBioguideID,
        setUserSenOneBioguideID,
        userSenTwoBioguideID,
        setUserSenTwoBioguideID,

        verifiedUser,
        setVerifiedUser,
        userDistrict,
        setUserDistrict,
        userState,
        setUserState,
        userZipcode,
        setUserZipcode,
        userFirstName,
        setUserFirstName,
        userLastName,
        setUserLastName,

        accountTypeName,
        hasSelectedAccountType,
        userUsername,
        isProfilePublic,
        requiresUsername,
        canCreateEvent,
        profileImage,
        entityName,
        municipalityName,
        municipalityDepartment,
        hasUsername,
        canAccessDashboard,
        isStripeVerified,
        politicianVerificationStatus,
        verificationRejectionReason,
        citizenManualVerificationStatus,
        hasAcceptedCommunityGuidelines,
        setHasAcceptedCommunityGuidelines,
        hasCompletedOnboarding,
        publicOfficeStatus,
        publicOfficeLevel,
        publicOfficePosition,
        addressSkipped,
        setAddressSkipped,

        loadUserContext,
        refreshFirebaseUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
